import React from "react";
import { Link } from "react-router-dom";
import "./LegalPages.css";

const PrivacyPolicyPage = () => {
  return (
    <main className="legal-page">
      <div className="legal-shell">
        <header className="legal-hero">
          <p className="legal-kicker">Legal</p>
          <h1>Privacy Policy</h1>
          <p className="legal-meta">
            Last Updated: May 16, 2026. This Privacy Policy explains how TattQR
            collects, uses, shares, stores, and protects personal information when
            you use our websites, applications, and QR-linked experience tools.
          </p>
        </header>

        <div className="legal-intro-grid">
          <section className="legal-panel">
            <h2>Policy Scope</h2>
            <p>
              This policy applies to information collected directly through TattQR
              platforms, customer support channels, and related service interactions.
              It does not govern third-party sites or services that we do not control.
            </p>
          </section>
          <section className="legal-panel">
            <h2>Quick Navigation</h2>
            <ol className="legal-toc">
              <li><a href="#data-collection">Information We Collect</a></li>
              <li><a href="#data-use">How We Use Information</a></li>
              <li><a href="#data-sharing">How We Share Information</a></li>
              <li><a href="#cookies">Cookies and Analytics</a></li>
              <li><a href="#retention">Data Retention</a></li>
              <li><a href="#rights">Privacy Rights and Choices</a></li>
              <li><a href="#security">Security</a></li>
              <li><a href="#children">Children's Privacy</a></li>
              <li><a href="#changes">Policy Updates</a></li>
              <li><a href="#contact">Contact</a></li>
            </ol>
          </section>
        </div>

        <div className="legal-content">
          <section className="legal-section" id="data-collection">
            <h2>1. Information We Collect</h2>
            <p>We collect information in the following categories:</p>
            <div className="legal-table-wrap">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Examples</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Account Information</td>
                    <td>Name, username, email, password hash, account preferences</td>
                    <td>Provided directly by you</td>
                  </tr>
                  <tr>
                    <td>Service Content</td>
                    <td>QR destinations, linked profile content, uploaded media</td>
                    <td>Provided directly by you</td>
                  </tr>
                  <tr>
                    <td>Usage and Device Data</td>
                    <td>IP address, browser type, device metadata, logs, events</td>
                    <td>Collected automatically</td>
                  </tr>
                  <tr>
                    <td>Communication Data</td>
                    <td>Support requests, feedback, and service-related messages</td>
                    <td>Provided during communication</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="legal-section" id="data-use">
            <h2>2. How We Use Information</h2>
            <p>We use personal information to:</p>
            <ul>
              <li>Provide, operate, and maintain TattQR services.</li>
              <li>Authenticate users and secure accounts.</li>
              <li>Process transactions and deliver support.</li>
              <li>
                Improve platform performance, reliability, safety, and user
                experience.
              </li>
              <li>Send service notices, security alerts, and account updates.</li>
              <li>Comply with legal obligations and enforce our terms.</li>
            </ul>
          </section>

          <section className="legal-section" id="data-sharing">
            <h2>3. How We Share Information</h2>
            <p>
              We do not sell your personal information. We may share limited
              information in these situations:
            </p>
            <ul>
              <li>
                With service providers that help us host infrastructure, process
                payments, run analytics, and support operations under contract.
              </li>
              <li>
                With legal authorities when required by law, subpoena, court order, or
                to protect rights, safety, and security.
              </li>
              <li>
                In connection with a merger, acquisition, financing, or asset transfer
                where permitted by law.
              </li>
            </ul>
          </section>

          <section className="legal-section" id="cookies">
            <h2>4. Cookies and Analytics</h2>
            <p>
              TattQR uses cookies and similar technologies to keep you signed in,
              remember settings, measure traffic, and improve performance. You can
              manage cookie preferences in browser settings; disabling cookies may
              impact certain features.
            </p>
          </section>

          <section className="legal-section" id="retention">
            <h2>5. Data Retention</h2>
            <p>
              We retain personal information for as long as necessary to provide the
              service, satisfy legal obligations, resolve disputes, and enforce
              agreements. Retention periods vary by data type, account status, and
              legal requirements.
            </p>
          </section>

          <section className="legal-section" id="rights">
            <h2>6. Privacy Rights and Choices</h2>
            <p>
              Depending on your location, you may have rights to access, correct,
              delete, or export personal information and to object to or limit certain
              processing.
            </p>
            <p>
              To submit a privacy request, email{" "}
              <a className="legal-inline-link" href="mailto:tattqr@gmail.com">
                tattqr@gmail.com
              </a>
              . We may request verification details before processing your request.
            </p>
            <div className="legal-note">
              California residents may also request information about categories of
              personal information collected, disclosed, or shared as required under
              California privacy laws.
            </div>
          </section>

          <section className="legal-section" id="security">
            <h2>7. Security</h2>
            <p>
              We apply administrative, technical, and organizational controls designed
              to protect personal information from unauthorized access, disclosure,
              alteration, and loss. No online system can be guaranteed fully secure.
            </p>
            <p>
              If you believe your account is compromised, contact us immediately at{" "}
              <a className="legal-inline-link" href="mailto:tattqr@gmail.com">
                tattqr@gmail.com
              </a>
              .
            </p>
          </section>

          <section className="legal-section" id="children">
            <h2>8. Children's Privacy</h2>
            <p>
              TattQR is not directed to children under 13, and we do not knowingly
              collect personal information from children under 13. If we learn that
              such information has been collected, we will delete it as required by
              law.
            </p>
          </section>

          <section className="legal-section" id="changes">
            <h2>9. Policy Updates</h2>
            <p>
              We may revise this Privacy Policy as our services evolve or legal
              requirements change. We will post the updated version with a revised
              "Last Updated" date and provide additional notice when required.
            </p>
          </section>

          <section className="legal-section" id="contact">
            <h2>10. Contact</h2>
            <p>
              For privacy questions or requests, contact TattQR at{" "}
              <a className="legal-inline-link" href="mailto:tattqr@gmail.com">
                tattqr@gmail.com
              </a>
              .
            </p>
            <p>
              Mailing address: TattQR, California, United States.
            </p>
          </section>
        </div>

        <div className="legal-actions">
          <Link className="legal-action-link" to="/terms-of-service">
            View Terms of Service
          </Link>
          <Link className="legal-action-link" to="/landing">
            Back to Landing
          </Link>
        </div>
      </div>
    </main>
  );
};

export default PrivacyPolicyPage;
